import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from 'sonner';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger, SheetFooter, SheetClose } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { ShoppingCart, User, Menu, X, Star, Plus, Minus, Trash2, ChefHat, Mic, MicOff, Phone, Package, Clock, Truck, Search, Heart, ArrowRight, Sparkles, Leaf, Timer, Users, Volume2, VolumeX } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const OPENAI_REALTIME_URL = process.env.REACT_APP_OPENAI_REALTIME_URL || 'https://api.openai.com/v1/realtime';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// ==================== CONTEXTS ====================

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, phone) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password, phone });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = async (idToken) => {
    const response = await axios.post(`${API}/auth/google`, { id_token: idToken });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, loginWithGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Cart Context
const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total_cents: 0 });
  const [loading, setLoading] = useState(false);

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart`);
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    }
  };

  const addToCart = async (productId, variantId, quantity = 1) => {
    setLoading(true);
    try {
      await axios.post(`${API}/cart/add`, { product_id: productId, variant_id: variantId, quantity });
      await fetchCart();
      toast.success('Added to cart!');
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (productId, variantId, quantity) => {
    setLoading(true);
    try {
      await axios.put(`${API}/cart/update`, { product_id: productId, variant_id: variantId, quantity });
      await fetchCart();
    } catch (error) {
      toast.error('Failed to update cart');
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart/clear`);
      setCart({ items: [], total_cents: 0 });
    } catch (error) {
      toast.error('Failed to clear cart');
    }
  };

  useEffect(() => {
    fetchCart();
  }, []);

  return (
    <CartContext.Provider value={{ cart, loading, addToCart, updateCartItem, clearCart, fetchCart }}>
      {children}
    </CartContext.Provider>
  );
};

// ==================== COMPONENTS ====================

// Navbar
const Navbar = () => {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const cartItemCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <nav className="sticky top-0 z-40 bg-cardamom/95 backdrop-blur-sm border-b border-stone-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <ChefHat className="h-8 w-8 text-saffron-600" />
            <span className="font-heading text-xl font-bold text-stone-800">Maniyaas</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/shop" className="text-stone-600 hover:text-saffron-600 transition-colors font-medium" data-testid="nav-shop">
              Shop
            </Link>
            <Link to="/shop?category=rice" className="text-stone-600 hover:text-saffron-600 transition-colors font-medium">
              Rice Mixes
            </Link>
            <Link to="/shop?category=sweets" className="text-stone-600 hover:text-saffron-600 transition-colors font-medium">
              Sweets
            </Link>
            <Link to="/shop?category=savories" className="text-stone-600 hover:text-saffron-600 transition-colors font-medium">
              Savories
            </Link>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="cart-button">
                  <ShoppingCart className="h-5 w-5" />
                  {cartItemCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-saffron-600 text-white text-xs flex items-center justify-center">
                      {cartItemCount}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-lg">
                <CartSheet />
              </SheetContent>
            </Sheet>

            {user ? (
              <div className="hidden md:flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => navigate('/orders')} data-testid="nav-orders">
                  <Package className="h-4 w-4 mr-2" />
                  Orders
                </Button>
                <Button variant="ghost" size="sm" onClick={logout} data-testid="nav-logout">
                  Logout
                </Button>
              </div>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => navigate('/auth')} className="hidden md:flex" data-testid="nav-login">
                <User className="h-4 w-4 mr-2" />
                Login
              </Button>
            )}

            {/* Mobile menu button */}
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-stone-200">
            <div className="flex flex-col gap-2">
              <Link to="/shop" className="px-4 py-2 text-stone-600 hover:bg-stone-100 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                Shop All
              </Link>
              <Link to="/shop?category=rice" className="px-4 py-2 text-stone-600 hover:bg-stone-100 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                Rice Mixes
              </Link>
              <Link to="/shop?category=sweets" className="px-4 py-2 text-stone-600 hover:bg-stone-100 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                Sweets
              </Link>
              <Link to="/shop?category=savories" className="px-4 py-2 text-stone-600 hover:bg-stone-100 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                Savories
              </Link>
              <Separator />
              {user ? (
                <>
                  <Link to="/orders" className="px-4 py-2 text-stone-600 hover:bg-stone-100 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                    My Orders
                  </Link>
                  <button className="px-4 py-2 text-left text-stone-600 hover:bg-stone-100 rounded-lg" onClick={() => { logout(); setMobileMenuOpen(false); }}>
                    Logout
                  </button>
                </>
              ) : (
                <Link to="/auth" className="px-4 py-2 text-stone-600 hover:bg-stone-100 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                  Login / Register
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Cart Sheet Content
const CartSheet = () => {
  const { cart, updateCartItem, clearCart } = useCart();
  const navigate = useNavigate();

  const formatPrice = (cents) => `₹${(cents / 100).toFixed(2)}`;

  if (cart.items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <ShoppingCart className="h-16 w-16 text-stone-300" />
        <p className="text-stone-500">Your cart is empty</p>
        <SheetClose asChild>
          <Button onClick={() => navigate('/shop')} data-testid="shop-now-btn">
            Start Shopping
          </Button>
        </SheetClose>
      </div>
    );
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle className="font-heading">Your Cart</SheetTitle>
        <SheetDescription>{cart.items.length} items</SheetDescription>
      </SheetHeader>

      <div className="flex-1 overflow-y-auto py-4 space-y-4">
        {cart.items.map((item, idx) => (
          <div key={idx} className="flex gap-3 p-3 bg-stone-50 rounded-xl" data-testid={`cart-item-${idx}`}>
            <img
              src={item.product_image || 'https://via.placeholder.com/80'}
              alt={item.product_name}
              className="w-20 h-20 object-cover rounded-lg"
            />
            <div className="flex-1">
              <h4 className="font-semibold text-stone-800">{item.product_name}</h4>
              <p className="text-sm text-stone-500">{item.variant_weight}</p>
              <p className="font-semibold text-saffron-600">{formatPrice(item.price_cents)}</p>

              <div className="flex items-center gap-2 mt-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => updateCartItem(item.product_id, item.variant_id, item.quantity - 1)}
                  data-testid={`cart-decrease-${idx}`}
                >
                  <Minus className="h-3 w-3" />
                </Button>
                <span className="w-8 text-center">{item.quantity}</span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => updateCartItem(item.product_id, item.variant_id, item.quantity + 1)}
                  data-testid={`cart-increase-${idx}`}
                >
                  <Plus className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-red-500 ml-auto"
                  onClick={() => updateCartItem(item.product_id, item.variant_id, 0)}
                  data-testid={`cart-remove-${idx}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <SheetFooter className="border-t pt-4">
        <div className="w-full space-y-4">
          <div className="flex justify-between text-lg font-semibold">
            <span>Total</span>
            <span className="text-saffron-600">{formatPrice(cart.total_cents)}</span>
          </div>
          <SheetClose asChild>
            <Button
              className="w-full bg-saffron-600 hover:bg-saffron-700 rounded-full"
              onClick={() => navigate('/checkout')}
              data-testid="checkout-btn"
            >
              Proceed to Checkout
            </Button>
          </SheetClose>
        </div>
      </SheetFooter>
    </>
  );
};

// Product Card
const ProductCard = ({ product }) => {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [selectedVariant, setSelectedVariant] = useState(product.variants?.[0] || null);

  const formatPrice = (cents) => `₹${(cents / 100).toFixed(0)}`;
  const price = product.base_price_cents + (selectedVariant?.price_adjustment_cents || 0);

  return (
    <Card className="product-card bg-cardamom border-0 shadow-sm hover:shadow-md overflow-hidden group" data-testid={`product-card-${product.slug}`}>
      <div className="relative aspect-square overflow-hidden">
        <img
          src={product.image_url || 'https://via.placeholder.com/400'}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onClick={() => navigate(`/product/${product.slug}`)}
        />
        {product.average_rating > 0 && (
          <Badge className="absolute top-3 left-3 bg-cardamom/90 text-stone-800">
            <Star className="h-3 w-3 fill-saffron-500 text-saffron-500 mr-1" />
            {product.average_rating}
          </Badge>
        )}
        <Badge className="absolute top-3 right-3 bg-curry-700 text-white capitalize">
          {product.category}
        </Badge>
      </div>

      <CardContent className="p-4">
        <h3
          className="font-heading text-lg font-semibold text-stone-800 cursor-pointer hover:text-saffron-600 transition-colors"
          onClick={() => navigate(`/product/${product.slug}`)}
        >
          {product.name}
        </h3>
        <p className="text-sm text-stone-500 mt-1 line-clamp-2">{product.description}</p>

        <div className="flex items-center justify-between mt-3">
          <span className="text-xl font-bold text-saffron-600">{formatPrice(price)}</span>
          {product.variants?.length > 0 && (
            <Select value={selectedVariant?.id} onValueChange={(val) => setSelectedVariant(product.variants.find(v => v.id === val))}>
              <SelectTrigger className="w-24 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {product.variants.map((variant) => (
                  <SelectItem key={variant.id} value={variant.id}>
                    {variant.weight_value}{variant.weight_unit}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </CardContent>

      <CardFooter className="p-4 pt-0 gap-2">
        <Button
          className="flex-1 bg-saffron-600 hover:bg-saffron-700 rounded-full"
          onClick={() => addToCart(product.id, selectedVariant?.id)}
          data-testid={`add-to-cart-${product.slug}`}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add to Cart
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="rounded-full"
          onClick={() => navigate(`/product/${product.slug}`)}
          data-testid={`view-product-${product.slug}`}
        >
          <ArrowRight className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
};

// Voice Chef Overlay
const VoiceChefOverlay = ({ isOpen, onClose, product }) => {
  const { token } = useAuth();
  const [status, setStatus] = useState('idle'); // idle, connecting, connected, speaking, listening, error
  const [transcript, setTranscript] = useState('');
  const [peerConnection, setPeerConnection] = useState(null);
  const [audioElement, setAudioElement] = useState(null);

  const startSession = async () => {
    if (!token) {
      toast.error('Please login to use Voice Chef');
      return;
    }

    setStatus('connecting');

    try {
      // Get session from backend
      const response = await axios.post(`${API}/voice/session`, { product_id: product.id });
      const { client_secret } = response.data;

      if (!client_secret) {
        throw new Error('Failed to get voice session');
      }

      // Create WebRTC connection
      const pc = new RTCPeerConnection();

      // Set up audio element
      const audio = document.createElement('audio');
      audio.autoplay = true;
      document.body.appendChild(audio);
      setAudioElement(audio);

      pc.ontrack = (event) => {
        audio.srcObject = event.streams[0];
        setStatus('connected');
      };

      // Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      // Create data channel
      const dc = pc.createDataChannel('oai-events');
      dc.onopen = () => {
        // Ask the assistant to greet first
        dc.send(JSON.stringify({
          type: 'response.create',
          response: {
            modalities: ['audio', 'text'],
            instructions: 'Start with a warm greeting, introduce yourself as Chef Maniyaa, and ask if the user is ready to begin'
          }
        }));
      };
      dc.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'response.audio_transcript.delta') {
          setTranscript(prev => prev + (data.delta || ''));
        } else if (data.type === 'response.audio_transcript.done') {
          setTranscript('');
        }
      };

      // Create offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Connect to OpenAI
      const sdpResponse = await fetch(`${OPENAI_REALTIME_URL}?model=gpt-4o-realtime-preview-2024-12-17`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${client_secret}`,
          'Content-Type': 'application/sdp'
        },
        body: offer.sdp
      });

      const answerSdp = await sdpResponse.text();
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

      setPeerConnection(pc);
      setStatus('connected');
      toast.success('Voice Chef connected!');

    } catch (error) {
      console.error('Voice session error:', error);
      setStatus('error');
      toast.error('Failed to connect Voice Chef. Make sure OpenAI API is configured.');
    }
  };

  const endSession = () => {
    if (peerConnection) {
      peerConnection.close();
      setPeerConnection(null);
    }
    if (audioElement) {
      audioElement.remove();
      setAudioElement(null);
    }
    setStatus('idle');
    setTranscript('');
    onClose();
  };

  useEffect(() => {
    return () => {
      if (peerConnection) {
        peerConnection.close();
      }
      if (audioElement) {
        audioElement.remove();
      }
    };
  }, []);

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && endSession()}>
      <DialogContent className="sm:max-w-lg bg-gradient-to-b from-stone-900 to-stone-950 text-white border-0">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <ChefHat className="h-6 w-6 text-saffron-500" />
            Chef Maniyaas
          </DialogTitle>
          <DialogDescription className="text-stone-400">
            Your AI cooking companion for {product?.name}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center py-8">
          {/* Voice Visualizer */}
          <div className={`w-32 h-32 rounded-full flex items-center justify-center mb-6 transition-all ${
            status === 'connected' ? 'bg-saffron-500/20 animate-pulse-slow' :
            status === 'connecting' ? 'bg-stone-700 animate-pulse' :
            'bg-stone-800'
          }`}>
            {status === 'connected' ? (
              <div className="flex items-end gap-1 h-12">
                {[1,2,3,4,5].map((i) => (
                  <div key={i} className="w-2 bg-saffron-500 rounded-full voice-bar" style={{ animationDelay: `${i * 0.1}s` }} />
                ))}
              </div>
            ) : status === 'connecting' ? (
              <div className="text-stone-400">Connecting...</div>
            ) : (
              <Mic className="h-12 w-12 text-stone-500" />
            )}
          </div>

          {/* Transcript */}
          {transcript && (
            <div className="text-center text-stone-300 px-4 py-2 bg-stone-800 rounded-lg mb-4 max-w-sm">
              {transcript}
            </div>
          )}

          {/* Status */}
          <p className="text-stone-400 text-sm mb-6">
            {status === 'idle' && 'Click Start to begin cooking with AI guidance'}
            {status === 'connecting' && 'Setting up your cooking session...'}
            {status === 'connected' && 'I\'m listening! Ask me anything about the recipe.'}
            {status === 'error' && 'Connection failed. Voice Chef requires OpenAI API key.'}
          </p>

          {/* Controls */}
          <div className="flex gap-3">
            {status === 'idle' || status === 'error' ? (
              <Button
                className="bg-saffron-600 hover:bg-saffron-700 rounded-full px-8"
                onClick={startSession}
                data-testid="start-voice-btn"
              >
                <Mic className="h-4 w-4 mr-2" />
                Start Cooking
              </Button>
            ) : (
              <Button
                variant="destructive"
                className="rounded-full px-8"
                onClick={endSession}
                data-testid="end-voice-btn"
              >
                <Phone className="h-4 w-4 mr-2" />
                End Session
              </Button>
            )}
          </div>
        </div>

        <div className="text-center text-xs text-stone-500 border-t border-stone-800 pt-4">
          Powered by OpenAI Realtime API • Your voice data is not stored
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Review Form Component
const ReviewForm = ({ productId, onSuccess }) => {
  const MAX_MEDIA_FILES = 6;
  const MAX_MEDIA_FILE_BYTES = 100 * 1024 * 1024;
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [body, setBody] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleFilesSelected = (event) => {
    const selected = Array.from(event.target.files || []);
    if (!selected.length) return;

    const validFiles = selected.filter((file) => {
      if (!(file.type.startsWith('image/') || file.type.startsWith('video/'))) {
        toast.error(`${file.name}: only images and videos are allowed`);
        return false;
      }
      if (file.size > MAX_MEDIA_FILE_BYTES) {
        toast.error(`${file.name}: file is larger than 100MB`);
        return false;
      }
      return true;
    });

    if (!validFiles.length) return;

    setMediaFiles((prev) => {
      const next = [...prev, ...validFiles];
      if (next.length > MAX_MEDIA_FILES) {
        toast.error(`You can upload up to ${MAX_MEDIA_FILES} files`);
      }
      return next.slice(0, MAX_MEDIA_FILES);
    });

    event.target.value = '';
  };

  const removeMediaFile = (index) => {
    setMediaFiles((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    if (!body.trim()) {
      toast.error('Please write your review');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('product_id', productId);
      formData.append('rating', String(rating));
      formData.append('body', body.trim());
      mediaFiles.forEach((file) => formData.append('media', file));

      await axios.post(`${API}/reviews`, formData);
      toast.success('Review submitted successfully!');
      setRating(0);
      setBody('');
      setMediaFiles([]);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-heading">Write a Review</DialogTitle>
        <DialogDescription>Share your experience with this product</DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-4 mt-4">
        <div>
          <Label className="mb-2 block">Your Rating</Label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="p-1 transition-transform hover:scale-110"
                data-testid={`star-${star}`}
              >
                <Star
                  className={`h-8 w-8 transition-colors ${
                    star <= (hoverRating || rating)
                      ? 'fill-saffron-500 text-saffron-500'
                      : 'text-stone-300'
                  }`}
                />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <p className="text-sm text-stone-500 mt-1">
              {rating === 1 && 'Poor'}
              {rating === 2 && 'Fair'}
              {rating === 3 && 'Good'}
              {rating === 4 && 'Very Good'}
              {rating === 5 && 'Excellent'}
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="review-body">Your Review</Label>
          <Textarea
            id="review-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Tell us about your experience with this product..."
            rows={4}
            className="mt-1"
            data-testid="review-body-input"
          />
        </div>

        <div>
          <Label htmlFor="review-media">Photos or Videos</Label>
          <Input
            id="review-media"
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleFilesSelected}
            className="mt-1"
            data-testid="review-media-input"
          />
          <p className="text-xs text-stone-500 mt-1">
            Up to 6 files. Images are auto-optimized for quality and size.
          </p>
          {mediaFiles.length > 0 && (
            <div className="mt-2 space-y-2">
              {mediaFiles.map((file, idx) => (
                <div key={`${file.name}-${idx}`} className="flex items-center justify-between rounded-md border border-stone-200 p-2 text-sm">
                  <span className="truncate pr-3">{file.name}</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2"
                    onClick={() => removeMediaFile(idx)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="submit"
            className="bg-saffron-600 hover:bg-saffron-700 rounded-full"
            disabled={loading}
            data-testid="submit-review-btn"
          >
            {loading ? 'Submitting...' : 'Submit Review'}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
};

// Footer
const Footer = () => (
  <footer className="bg-stone-900 text-stone-300 py-12 mt-auto">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <ChefHat className="h-8 w-8 text-saffron-500" />
            <span className="font-heading text-xl font-bold text-white">Maniyaas</span>
          </div>
          <p className="text-sm font-accent text-lg text-saffron-400">"Tradition in a Packet, Chef in Your Pocket"</p>
        </div>

        <div>
          <h4 className="font-semibold text-white mb-4">Shop</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/shop?category=rice" className="hover:text-saffron-500 transition-colors">Rice Mixes</Link></li>
            <li><Link to="/shop?category=sambar" className="hover:text-saffron-500 transition-colors">Sambar & Rasam</Link></li>
            <li><Link to="/shop?category=sweets" className="hover:text-saffron-500 transition-colors">Sweets</Link></li>
            <li><Link to="/shop?category=savories" className="hover:text-saffron-500 transition-colors">Savories</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold text-white mb-4">Support</h4>
          <ul className="space-y-2 text-sm">
            <li><a href="#" className="hover:text-saffron-500 transition-colors">Contact Us</a></li>
            <li><a href="#" className="hover:text-saffron-500 transition-colors">Shipping Info</a></li>
            <li><a href="#" className="hover:text-saffron-500 transition-colors">Returns</a></li>
            <li><a href="#" className="hover:text-saffron-500 transition-colors">FAQ</a></li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold text-white mb-4">Connect</h4>
          <p className="text-sm mb-4">Get updates on new products and recipes!</p>
          <div className="flex gap-2">
            <Input placeholder="Enter email" className="bg-stone-800 border-stone-700" />
            <Button className="bg-saffron-600 hover:bg-saffron-700">Join</Button>
          </div>
        </div>
      </div>

      <Separator className="my-8 bg-stone-800" />

      <div className="flex flex-col md:flex-row justify-between items-center text-sm text-stone-500">
        <p>© 2026 Maniyaas Foods. Made with love in India.</p>
        <div className="flex gap-4 mt-4 md:mt-0">
          <a href="#" className="hover:text-white">Privacy</a>
          <a href="#" className="hover:text-white">Terms</a>
        </div>
      </div>
    </div>
  </footer>
);

// ==================== PAGES ====================

// Home Page
const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Seed data first
        await axios.post(`${API}/seed`).catch(() => {});
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const totalReviews = products.reduce((sum, product) => sum + (product.review_count || 0), 0);
  const weightedRatingSum = products.reduce(
    (sum, product) => sum + (product.average_rating || 0) * (product.review_count || 0),
    0
  );
  const overallRating = totalReviews > 0 ? (weightedRatingSum / totalReviews).toFixed(1) : null;

  const categories = [
    { name: 'Rice Mixes', category: 'rice', image: '/assets/images/gheerice-landscape.webp' },
    { name: 'Sweets', category: 'sweets', image: '/assets/images/athimadhura.png' },
    { name: 'Savories', category: 'savories', image: '/assets/images/gojjavalakki.png' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-orange-50 to-stone-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-curry-700 text-white mb-4">
                <Leaf className="h-3 w-3 mr-1" /> 100% Vegetarian
              </Badge>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 tracking-tight mb-6">
                Home-cooked taste,
                <span className="gradient-text block">Zero effort.</span>
              </h1>
              <p className="text-lg text-stone-600 mb-8 max-w-lg">
                Pure ingredients. Zero shortcuts. Just real food the way it’s meant to be.

                Maniyaa’s brings home the taste of tradition — honest, wholesome, unforgettable.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="bg-saffron-600 hover:bg-saffron-700 rounded-full shadow-lg hover:shadow-orange-200 transition-all"
                  onClick={() => navigate('/shop')}
                  data-testid="hero-shop-btn"
                >
                  Shop Now
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => navigate('/shop')}
                >
                  <ChefHat className="h-5 w-5 mr-2" />
                  Meet AI Chef
                </Button>
              </div>

              {/* Trust badges */}
              <div className="flex items-center gap-6 mt-8 text-sm text-stone-600">
                <div className="flex items-center gap-1">
                  <Star className="h-4 w-4 fill-saffron-500 text-saffron-500" />
                  {overallRating ? (
                    <span>{overallRating}/5 Rating</span>
                  ) : (
                    <span>New</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Truck className="h-4 w-4 text-curry-700" />
                  <span>Pan India Delivery</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="/assets/images/all-foods.png"
                alt="Indian Food Spread"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-cardamom p-4 rounded-xl shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-r from-saffron-500 to-red-500 flex items-center justify-center">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-stone-800">AI Voice Chef</p>
                    <p className="text-xs text-stone-500">Step-by-step guidance</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Bento Grid */}
      <section className="py-16 md:py-24 bg-basmati">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-stone-900 mb-4">
              Explore Our Kitchen
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              From aromatic rice dishes to crispy savories and melt-in-mouth sweets — we've got your cravings covered.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 auto-rows-[minmax(180px,auto)]">
            {categories.map((cat, idx) => (
              <Card
                key={cat.category}
                className={`overflow-hidden cursor-pointer group border-0 shadow-md hover:shadow-xl transition-all ${
                  idx === 0 ? 'md:col-span-2 md:row-span-2' : ''
                }`}
                onClick={() => navigate(`/shop?category=${cat.category}`)}
                data-testid={`category-${cat.category}`}
              >
                <div className="relative h-full">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <h3 className="font-heading text-xl font-bold text-white">{cat.name}</h3>
                    <p className="text-white/80 text-sm flex items-center gap-1 mt-1">
                      Explore <ArrowRight className="h-4 w-4" />
                    </p>
                  </div>
                </div>
              </Card>
            ))}

            {/* Voice Chef Feature Card */}
            <Card className="bg-gradient-to-br from-stone-900 to-stone-800 text-white border-0 p-6 flex flex-col justify-between">
              <div>
                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-saffron-500 to-red-500 flex items-center justify-center mb-4">
                  <Mic className="h-6 w-6" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-2">AI Voice Chef</h3>
                <p className="text-stone-400 text-sm">Get real-time cooking guidance with our AI assistant</p>
              </div>
              <Button variant="secondary" className="mt-4 rounded-full" onClick={() => navigate('/shop')}>
                Try Now
              </Button>
            </Card>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-3xl font-bold text-stone-900">Bestsellers</h2>
              <p className="text-stone-600">Most loved by our customers</p>
            </div>
            <Button variant="outline" className="rounded-full" onClick={() => navigate('/shop')} data-testid="view-all-btn">
              View All
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {loading ? (
              Array(4).fill(0).map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <div className="aspect-square bg-stone-200" />
                  <CardContent className="p-4">
                    <div className="h-4 bg-stone-200 rounded mb-2" />
                    <div className="h-3 bg-stone-200 rounded w-2/3" />
                  </CardContent>
                </Card>
              ))
            ) : (
              products.slice(0, 4).map(product => (
                <ProductCard key={product.id} product={product} />
              ))
            )}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="h-14 w-14 rounded-full bg-saffron-100 flex items-center justify-center mx-auto mb-4">
                <Timer className="h-7 w-7 text-saffron-600" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">Ready in Minutes</h3>
              <p className="text-stone-600 text-sm">Restaurant-quality meals in under 20 minutes</p>
            </div>
            <div className="text-center p-6">
              <div className="h-14 w-14 rounded-full bg-curry-100 flex items-center justify-center mx-auto mb-4">
                <Leaf className="h-7 w-7 text-curry-700" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">100% Natural</h3>
              <p className="text-stone-600 text-sm">No preservatives, no artificial flavors</p>
            </div>
            <div className="text-center p-6">
              <div className="h-14 w-14 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
                <Users className="h-7 w-7 text-orange-600" />
              </div>
              <h3 className="font-heading text-lg font-semibold mb-2">Family Recipes</h3>
              <p className="text-stone-600 text-sm">Passed down through generations</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Shop Page
const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const categoryFilter = searchParams.get('category');

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        let url = `${API}/products`;
        const params = new URLSearchParams();
        if (categoryFilter) params.append('category', categoryFilter);
        if (searchQuery) params.append('search', searchQuery);
        if (params.toString()) url += `?${params.toString()}`;

        const response = await axios.get(url);
        setProducts(response.data);
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [categoryFilter, searchQuery]);

  return (
    <div className="min-h-screen bg-basmati py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-stone-900" data-testid="shop-title">
              {categoryFilter ? `${categoryFilter.charAt(0).toUpperCase() + categoryFilter.slice(1)}` : 'All Products'}
            </h1>
            <p className="text-stone-600">{products.length} products</p>
          </div>

          <div className="flex gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                placeholder="Search products..."
                className="pl-10 w-64"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-input"
              />
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <Tabs defaultValue={categoryFilter || 'all'} className="mb-8">
          <TabsList className="bg-cardamom">
            <TabsTrigger value="all" asChild>
              <Link to="/shop">All</Link>
            </TabsTrigger>
            <TabsTrigger value="rice" asChild>
              <Link to="/shop?category=rice">Rice</Link>
            </TabsTrigger>
            <TabsTrigger value="sambar" asChild>
              <Link to="/shop?category=sambar">Sambar</Link>
            </TabsTrigger>
            <TabsTrigger value="sweets" asChild>
              <Link to="/shop?category=sweets">Sweets</Link>
            </TabsTrigger>
            <TabsTrigger value="savories" asChild>
              <Link to="/shop?category=savories">Savories</Link>
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="products-grid">
          {loading ? (
            Array(8).fill(0).map((_, i) => (
              <Card key={i} className="animate-pulse">
                <div className="aspect-square bg-stone-200" />
                <CardContent className="p-4">
                  <div className="h-4 bg-stone-200 rounded mb-2" />
                  <div className="h-3 bg-stone-200 rounded w-2/3" />
                </CardContent>
              </Card>
            ))
          ) : products.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <Package className="h-16 w-16 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">No products found</p>
            </div>
          ) : (
            products.map(product => (
              <ProductCard key={product.id} product={product} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Product Detail Page
const ProductDetailPage = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [voiceChefOpen, setVoiceChefOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${slug}`);
        setProduct(response.data);
        setSelectedVariant(response.data.variants?.[0] || null);

        // Fetch reviews
        const reviewsResponse = await axios.get(`${API}/reviews/${response.data.id}`);
        setReviews(reviewsResponse.data);
      } catch (error) {
        console.error('Failed to fetch product:', error);
        navigate('/shop');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-basmati flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-saffron-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!product) return null;

  const formatPrice = (cents) => `₹${(cents / 100).toFixed(0)}`;
  const price = product.base_price_cents + (selectedVariant?.price_adjustment_cents || 0);
  const recipe = product.recipe;

  return (
    <div className="min-h-screen bg-basmati py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* Image */}
          <div className="relative">
            <img
              src={product.image_url || 'https://via.placeholder.com/600'}
              alt={product.name}
              className="w-full aspect-square object-cover rounded-2xl shadow-lg"
            />
            <Badge className="absolute top-4 left-4 bg-curry-700 text-white capitalize">
              {product.category}
            </Badge>
            {product.average_rating > 0 && (
              <Badge className="absolute top-4 right-4 bg-cardamom/90 text-stone-800">
                <Star className="h-3 w-3 fill-saffron-500 text-saffron-500 mr-1" />
                {product.average_rating} ({product.review_count} reviews)
              </Badge>
            )}
          </div>

          {/* Details */}
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl font-bold text-stone-900 mb-4" data-testid="product-title">
              {product.name}
            </h1>
            <p className="text-stone-600 mb-6">{product.description}</p>

            {/* Price & Variant */}
            <div className="flex items-center gap-4 mb-6">
              <span className="text-3xl font-bold text-saffron-600" data-testid="product-price">
                {formatPrice(price * quantity)}
              </span>
              {product.variants?.length > 0 && (
                <Select value={selectedVariant?.id} onValueChange={(val) => setSelectedVariant(product.variants.find(v => v.id === val))}>
                  <SelectTrigger className="w-32" data-testid="variant-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {product.variants.map((variant) => (
                      <SelectItem key={variant.id} value={variant.id}>
                        {variant.weight_value}{variant.weight_unit}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Quantity */}
            <div className="flex items-center gap-4 mb-6">
              <Label>Quantity</Label>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  data-testid="decrease-qty"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-12 text-center font-semibold">{quantity}</span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(quantity + 1)}
                  data-testid="increase-qty"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4 mb-8">
              <Button
                size="lg"
                className="flex-1 bg-saffron-600 hover:bg-saffron-700 rounded-full"
                onClick={() => {
                  for (let i = 0; i < quantity; i++) {
                    addToCart(product.id, selectedVariant?.id);
                  }
                }}
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                Add to Cart
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full border-saffron-600 text-saffron-600 hover:bg-saffron-50"
                onClick={() => setVoiceChefOpen(true)}
                data-testid="voice-chef-btn"
              >
                <ChefHat className="h-5 w-5 mr-2" />
                Cook with AI Chef
              </Button>
            </div>

            {/* Recipe Info */}
            {recipe && (
              <Card className="bg-stone-50 border-0 mb-6">
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-3">Recipe Details</h3>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <Users className="h-5 w-5 mx-auto text-stone-400 mb-1" />
                      <p className="text-sm font-medium">{recipe.servings_base} servings</p>
                    </div>
                    <div>
                      <Clock className="h-5 w-5 mx-auto text-stone-400 mb-1" />
                      <p className="text-sm font-medium">{recipe.prep_time_mins} min prep</p>
                    </div>
                    <div>
                      <Timer className="h-5 w-5 mx-auto text-stone-400 mb-1" />
                      <p className="text-sm font-medium">{recipe.cook_time_mins} min cook</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Ingredients */}
            <div className="mb-6">
              <h3 className="font-semibold mb-2">Ingredients</h3>
              <p className="text-stone-600 text-sm">{product.ingredients_text}</p>
            </div>

            {/* Cooking Steps */}
            {recipe?.steps?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3">How to Cook</h3>
                <ol className="space-y-3">
                  {recipe.steps.map((step, idx) => (
                    <li key={idx} className="flex gap-3">
                      <span className="h-6 w-6 rounded-full bg-saffron-100 text-saffron-600 flex items-center justify-center text-sm font-medium shrink-0">
                        {idx + 1}
                      </span>
                      <p className="text-stone-600 text-sm">{step.instruction || step}</p>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>

        {/* Reviews Section */}
        <section className="mt-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl font-bold text-stone-900">Customer Reviews</h2>
            {user && (
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-full" data-testid="write-review-btn">
                    <Star className="h-4 w-4 mr-2" />
                    Write a Review
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <ReviewForm productId={product.id} onSuccess={async () => {
                    const reviewsResponse = await axios.get(`${API}/reviews/${product.id}`);
                    setReviews(reviewsResponse.data);
                    // Refresh product to get updated rating
                    const productResponse = await axios.get(`${API}/products/${slug}`);
                    setProduct(productResponse.data);
                  }} />
                </DialogContent>
              </Dialog>
            )}
          </div>

          {!user && (
            <Card className="mb-6 bg-saffron-50 border-saffron-200">
              <CardContent className="p-4 flex items-center justify-between">
                <p className="text-stone-600">Login to write a review</p>
                <Button variant="outline" size="sm" onClick={() => navigate('/auth')} data-testid="login-to-review-btn">
                  Login
                </Button>
              </CardContent>
            </Card>
          )}

          {reviews.length === 0 ? (
            <Card className="text-center py-8">
              <CardContent>
                <p className="text-stone-500">No reviews yet. Be the first to review this product!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <Card key={review.id} className="border-0 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Avatar>
                        <AvatarImage src={review.user_profile_photo_url || ''} alt={review.user_name} />
                        <AvatarFallback>{(review.user_name || 'U').charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold">{review.user_name}</span>
                          {review.verified_purchase && (
                            <Badge variant="secondary" className="text-xs">Verified Purchase</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1 mb-2">
                          {Array(5).fill(0).map((_, i) => (
                            <Star
                              key={i}
                              className={`h-4 w-4 ${i < review.rating ? 'fill-saffron-500 text-saffron-500' : 'text-stone-300'}`}
                            />
                          ))}
                        </div>
                        <p className="text-stone-600 text-sm">{review.body}</p>
                        {!!review.media?.length && (
                          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {review.media.map((mediaItem, mediaIndex) => (
                              <div key={`${review.id}-media-${mediaIndex}`} className="rounded-lg overflow-hidden border border-stone-200 bg-stone-50">
                                {mediaItem.kind === 'image' ? (
                                  <img
                                    src={mediaItem.url}
                                    alt="Review media"
                                    loading="lazy"
                                    className="w-full h-48 object-cover"
                                  />
                                ) : (
                                  <video controls preload="metadata" className="w-full h-48 object-cover bg-black">
                                    <source src={mediaItem.url} type={mediaItem.content_type || 'video/mp4'} />
                                    Your browser does not support video playback.
                                  </video>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Voice Chef Modal */}
      <VoiceChefOverlay
        isOpen={voiceChefOpen}
        onClose={() => setVoiceChefOpen(false)}
        product={product}
      />
    </div>
  );
};

// Auth Page
const AuthPage = () => {
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const { login, register, loginWithGoogle, user } = useAuth();
  const navigate = useNavigate();
  const googleButtonRef = useRef(null);
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: ''
  });

  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  useEffect(() => {
    if (!googleClientId) return;
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      if (window.google?.accounts?.id && googleButtonRef.current) {
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: async (response) => {
            setLoading(true);
            try {
              await loginWithGoogle(response.credential);
              toast.success('Welcome!');
              navigate('/');
            } catch (error) {
              toast.error(error.response?.data?.detail || 'Google sign-in failed');
            } finally {
              setLoading(false);
            }
          },
          auto_select: false,
          ux_mode: 'popup',
        });
        googleButtonRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleButtonRef.current, {
          theme: 'outline',
          size: 'large',
          shape: 'pill',
          text: 'signin_with',
          width: 320,
        });
        clearInterval(interval);
      } else if (attempts > 20) {
        clearInterval(interval);
      }
    }, 250);

    return () => clearInterval(interval);
  }, [googleClientId, loginWithGoogle, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      } else {
        await register(formData.name, formData.email, formData.password, formData.phone);
        toast.success('Account created!');
      }
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-basmati flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-0 shadow-xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <ChefHat className="h-12 w-12 text-saffron-600" />
          </div>
          <CardTitle className="font-heading text-2xl">
            {mode === 'login' ? 'Welcome Back' : 'Create Account'}
          </CardTitle>
          <CardDescription>
            {mode === 'login' ? 'Sign in to continue shopping' : 'Join Maniyaas Foods today'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="name-input"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                data-testid="password-input"
              />
            </div>

            {mode === 'register' && (
              <div>
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  data-testid="phone-input"
                />
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-saffron-600 hover:bg-saffron-700 rounded-full"
              disabled={loading}
              data-testid="auth-submit-btn"
            >
              {loading ? 'Please wait...' : (mode === 'login' ? 'Sign In' : 'Create Account')}
            </Button>
          </form>

          {googleClientId && (
            <div className="mt-6">
              <div className="flex items-center gap-3 text-xs text-stone-400">
                <span className="h-px flex-1 bg-stone-200" />
                <span>OR</span>
                <span className="h-px flex-1 bg-stone-200" />
              </div>
              <div className="mt-4 flex justify-center">
                <div ref={googleButtonRef} />
              </div>
            </div>
          )}
        </CardContent>

        <CardFooter className="justify-center">
          <p className="text-sm text-stone-600">
            {mode === 'login' ? "Don't have an account? " : "Already have an account? "}
            <button
              className="text-saffron-600 font-medium hover:underline"
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              data-testid="toggle-auth-mode"
            >
              {mode === 'login' ? 'Sign Up' : 'Sign In'}
            </button>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

// Checkout Page
const CheckoutPage = () => {
  const { cart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const placesServicesRef = useRef({ autocompleteService: null, placesService: null, sessionToken: null });
  const [loading, setLoading] = useState(false);
  const [pincode, setPincode] = useState('');
  const [serviceability, setServiceability] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(true);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [savingAddress, setSavingAddress] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [placesReady, setPlacesReady] = useState(false);
  const [addressSearchQuery, setAddressSearchQuery] = useState('');
  const [addressPredictions, setAddressPredictions] = useState([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState({
    label: '',
    line1: '',
    city: '',
    state: '',
    pincode: '',
    phone: user?.phone || ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/auth');
    }
    if (cart.items.length === 0) {
      navigate('/shop');
    }
  }, [user, cart, navigate]);

  const fetchAddresses = useCallback(async () => {
    setAddressesLoading(true);
    try {
      const response = await axios.get(`${API}/addresses`);
      const loadedAddresses = response.data || [];
      setAddresses(loadedAddresses);
      if (loadedAddresses.length > 0) {
        setSelectedAddressId((prev) => prev || loadedAddresses[0].id);
      } else {
        setSelectedAddressId('');
      }
    } catch (error) {
      toast.error('Failed to load addresses');
    } finally {
      setAddressesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchAddresses();
      if (user.phone) {
        setAddressForm((prev) => ({ ...prev, phone: prev.phone || user.phone }));
      }
    }
  }, [user, fetchAddresses]);

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) {
      return;
    }

    const setupPlaces = () => {
      if (!window.google?.maps?.places) {
        return;
      }
      placesServicesRef.current = {
        autocompleteService: new window.google.maps.places.AutocompleteService(),
        placesService: new window.google.maps.places.PlacesService(document.createElement('div')),
        sessionToken: new window.google.maps.places.AutocompleteSessionToken(),
      };
      setPlacesReady(true);
    };

    if (window.google?.maps?.places) {
      setupPlaces();
      return;
    }

    const existingScript = document.getElementById('google-maps-places-script');
    if (existingScript) {
      existingScript.addEventListener('load', setupPlaces);
      return () => existingScript.removeEventListener('load', setupPlaces);
    }

    const script = document.createElement('script');
    script.id = 'google-maps-places-script';
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.addEventListener('load', setupPlaces);
    script.addEventListener('error', () => toast.error('Failed to load Google Places'));
    document.head.appendChild(script);

    return () => {
      script.removeEventListener('load', setupPlaces);
    };
  }, []);

  useEffect(() => {
    if (!placesReady || !addressSearchQuery || addressSearchQuery.trim().length < 3) {
      setAddressPredictions([]);
      return;
    }

    const timeout = setTimeout(() => {
      const service = placesServicesRef.current.autocompleteService;
      if (!service) {
        return;
      }
      setIsSearchingAddress(true);
      service.getPlacePredictions(
        {
          input: addressSearchQuery,
          componentRestrictions: { country: 'in' },
          sessionToken: placesServicesRef.current.sessionToken,
          types: ['address'],
        },
        (predictions, status) => {
          setIsSearchingAddress(false);
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
            setAddressPredictions([]);
            return;
          }
          setAddressPredictions(predictions);
        }
      );
    }, 250);

    return () => clearTimeout(timeout);
  }, [placesReady, addressSearchQuery]);

  const formatPrice = (cents) => `₹${(cents / 100).toFixed(2)}`;

  const checkPincode = async () => {
    try {
      const response = await axios.post(`${API}/shipping/check-serviceability`, { pincode });
      setServiceability(response.data);
      if (response.data.serviceable) {
        setAddressForm((prev) => ({ ...prev, pincode }));
      }
    } catch (error) {
      toast.error('Failed to check pincode');
    }
  };

  const applyCoupon = async () => {
    try {
      const response = await axios.post(`${API}/coupons/validate`, { code: couponCode });
      setCouponApplied(response.data);
      toast.success('Coupon applied!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon');
      setCouponApplied(null);
    }
  };

  const calculateDiscount = () => {
    if (!couponApplied) return 0;
    if (cart.total_cents < couponApplied.min_order_value_cents) return 0;

    if (couponApplied.discount_type === 'percentage') {
      return Math.floor(cart.total_cents * couponApplied.discount_value / 100);
    }
    return couponApplied.discount_value * 100;
  };

  const finalTotal = cart.total_cents - calculateDiscount();

  const parsePlaceAddress = useCallback((place) => {
    const components = place.address_components || [];
    const getComponent = (type) => components.find((component) => component.types.includes(type))?.long_name || '';
    const streetNumber = getComponent('street_number');
    const route = getComponent('route');
    const sublocality = getComponent('sublocality_level_1') || getComponent('sublocality') || getComponent('neighborhood');
    const city = getComponent('locality') || getComponent('postal_town') || getComponent('administrative_area_level_2');
    const state = getComponent('administrative_area_level_1');
    const postalCode = getComponent('postal_code');
    const line1 = [streetNumber, route, sublocality].filter(Boolean).join(', ');

    return {
      line1: line1 || place.formatted_address?.split(',').slice(0, 2).join(', ') || '',
      city,
      state,
      pincode: postalCode,
    };
  }, []);

  const handleSelectPrediction = useCallback((prediction) => {
    const service = placesServicesRef.current.placesService;
    if (!service) {
      return;
    }

    service.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['address_components', 'formatted_address'],
        sessionToken: placesServicesRef.current.sessionToken,
      },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          toast.error('Failed to fetch selected address details');
          return;
        }

        const parsed = parsePlaceAddress(place);
        setAddressForm((prev) => ({
          ...prev,
          line1: parsed.line1 || prev.line1,
          city: parsed.city || prev.city,
          state: parsed.state || prev.state,
          pincode: parsed.pincode || prev.pincode,
        }));

        if (parsed.pincode) {
          setPincode(parsed.pincode);
        }

        setAddressSearchQuery(prediction.description);
        setAddressPredictions([]);
        placesServicesRef.current.sessionToken = new window.google.maps.places.AutocompleteSessionToken();
      }
    );
  }, [parsePlaceAddress]);

  const resetAddressForm = useCallback(() => {
    setEditingAddressId(null);
    setAddressForm({
      label: '',
      line1: '',
      city: '',
      state: '',
      pincode: pincode || '',
      phone: user?.phone || ''
    });
    setAddressSearchQuery('');
    setAddressPredictions([]);
  }, [pincode, user]);

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    if (!addressForm.line1 || !addressForm.city || !addressForm.state || !addressForm.pincode || !addressForm.phone) {
      toast.error('Please fill all required address fields');
      return;
    }

    setSavingAddress(true);
    try {
      if (editingAddressId) {
        const response = await axios.put(`${API}/addresses/${editingAddressId}`, addressForm);
        const updated = response.data;
        setAddresses((prev) => prev.map((address) => (address.id === updated.id ? updated : address)));
        toast.success('Address updated');
      } else {
        const response = await axios.post(`${API}/addresses`, addressForm);
        const created = response.data;
        setAddresses((prev) => [created, ...prev]);
        setSelectedAddressId(created.id);
        toast.success('Address added');
      }
      resetAddressForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save address');
    } finally {
      setSavingAddress(false);
    }
  };

  const startEditAddress = (address) => {
    setEditingAddressId(address.id);
    setAddressSearchQuery('');
    setAddressPredictions([]);
    setAddressForm({
      label: address.label || '',
      line1: address.line1,
      city: address.city,
      state: address.state,
      pincode: address.pincode,
      phone: address.phone,
    });
  };

  const handleDeleteAddress = async (addressId) => {
    try {
      await axios.delete(`${API}/addresses/${addressId}`);
      setAddresses((prev) => {
        const filtered = prev.filter((address) => address.id !== addressId);
        if (selectedAddressId === addressId) {
          setSelectedAddressId(filtered[0]?.id || '');
        }
        return filtered;
      });
      if (editingAddressId === addressId) {
        resetAddressForm();
      }
      toast.success('Address deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete address');
    }
  };

  const handlePayment = async () => {
    if (!selectedAddressId) {
      toast.error('Please select a delivery address');
      return;
    }

    setLoading(true);

    try {
      // Create order
      const orderResponse = await axios.post(`${API}/orders/create`, {
        address_id: selectedAddressId,
        coupon_code: couponCode || null
      });

      const order = orderResponse.data;

      // Initiate PhonePe payment
      const paymentResponse = await axios.post(`${API}/payments/initiate`, { order_id: order.id });
      const paymentData = paymentResponse.data;

      if (paymentData.success && paymentData.redirect_url) {
        // Store order ID in session for callback
        sessionStorage.setItem('pending_order_id', order.id);
        // Redirect to PhonePe payment page
        window.location.href = paymentData.redirect_url;
      } else {
        toast.error(paymentData.message || 'Failed to initiate payment');
      }

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-basmati py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-heading text-3xl font-bold text-stone-900 mb-8" data-testid="checkout-title">Checkout</h1>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Address & Delivery */}
          <div className="md:col-span-2 space-y-6">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Step 1: Check Delivery</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter pincode"
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value)}
                    maxLength={6}
                    data-testid="pincode-input"
                  />
                  <Button onClick={checkPincode} variant="outline" data-testid="check-pincode-btn">
                    Check
                  </Button>
                </div>
                {serviceability && (
                  <div className={`mt-3 p-3 rounded-lg ${serviceability.serviceable ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                    {serviceability.serviceable ? (
                      <p className="flex items-center gap-2">
                        <Truck className="h-4 w-4" />
                        Delivery available! Estimated: {serviceability.estimated_days} days
                      </p>
                    ) : (
                      <p>{serviceability.message}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Step 2: Select Address</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {addressesLoading ? (
                  <p className="text-sm text-stone-500">Loading addresses...</p>
                ) : addresses.length === 0 ? (
                  <p className="text-sm text-stone-500">No saved addresses yet. Add one below.</p>
                ) : (
                  <div className="space-y-3">
                    {addresses.map((address) => (
                      <label
                        key={address.id}
                        className={`flex gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                          selectedAddressId === address.id ? 'border-saffron-500 bg-saffron-50' : 'border-stone-200'
                        }`}
                        data-testid={`address-option-${address.id}`}
                      >
                        <input
                          type="radio"
                          name="selected-address"
                          value={address.id}
                          checked={selectedAddressId === address.id}
                          onChange={() => setSelectedAddressId(address.id)}
                          className="mt-1"
                        />
                        <div className="flex-1 text-sm text-stone-700">
                          {address.label && <p className="font-medium">{address.label}</p>}
                          <p>{address.line1}</p>
                          <p>{address.city}, {address.state} - {address.pincode}</p>
                          <p>Phone: {address.phone}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              startEditAddress(address);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={(e) => {
                              e.preventDefault();
                              handleDeleteAddress(address.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">{editingAddressId ? 'Edit Address' : 'Step 3: Add New Address'}</CardTitle>
              </CardHeader>
              <CardContent>
                <form className="space-y-4" onSubmit={handleSaveAddress}>
                  <div>
                    <Label>Address Label (optional)</Label>
                    <Input
                      value={addressForm.label}
                      onChange={(e) => setAddressForm({ ...addressForm, label: e.target.value })}
                      placeholder="Home, Work, etc."
                    />
                  </div>
                  <div className="relative">
                    <Label>Search Address (Google, optional)</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                      <Input
                        value={addressSearchQuery}
                        onChange={(e) => setAddressSearchQuery(e.target.value)}
                        placeholder="Start typing your address..."
                        className="pl-9"
                        disabled={!GOOGLE_MAPS_API_KEY}
                        data-testid="google-address-search-input"
                      />
                    </div>
                    {!GOOGLE_MAPS_API_KEY && (
                      <p className="text-xs text-stone-500 mt-1">Set `REACT_APP_GOOGLE_MAPS_API_KEY` to enable autocomplete.</p>
                    )}
                    {GOOGLE_MAPS_API_KEY && !placesReady && (
                      <p className="text-xs text-stone-500 mt-1">Loading Google Places...</p>
                    )}
                    {GOOGLE_MAPS_API_KEY && isSearchingAddress && addressSearchQuery.trim().length >= 3 && (
                      <p className="text-xs text-stone-500 mt-1">Searching addresses...</p>
                    )}
                    {addressPredictions.length > 0 && (
                      <div className="absolute z-20 mt-1 w-full rounded-md border border-stone-200 bg-white shadow-lg overflow-hidden">
                        {addressPredictions.map((prediction) => (
                          <button
                            key={prediction.place_id}
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm text-stone-700 hover:bg-stone-100"
                            onClick={() => handleSelectPrediction(prediction)}
                          >
                            {prediction.description}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Textarea
                      value={addressForm.line1}
                      onChange={(e) => setAddressForm({ ...addressForm, line1: e.target.value })}
                      placeholder="House/Flat No., Street, Area"
                      data-testid="address-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>City</Label>
                      <Input
                        value={addressForm.city}
                        onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                        data-testid="city-input"
                      />
                    </div>
                    <div>
                      <Label>State</Label>
                      <Input
                        value={addressForm.state}
                        onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                        data-testid="state-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Pincode</Label>
                      <Input
                        value={addressForm.pincode}
                        onChange={(e) => setAddressForm({ ...addressForm, pincode: e.target.value })}
                        maxLength={6}
                        data-testid="shipping-pincode-input"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={addressForm.phone}
                        onChange={(e) => setAddressForm({ ...addressForm, phone: e.target.value })}
                        data-testid="phone-checkout-input"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={savingAddress}>
                      {savingAddress ? 'Saving...' : (editingAddressId ? 'Update Address' : 'Save Address')}
                    </Button>
                    {editingAddressId && (
                      <Button type="button" variant="outline" onClick={resetAddressForm}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div>
            <Card className="border-0 shadow-sm sticky top-24">
              <CardHeader>
                <CardTitle className="text-lg">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {cart.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-stone-600">{item.product_name} x {item.quantity}</span>
                    <span>{formatPrice(item.total_cents)}</span>
                  </div>
                ))}

                <Separator />

                <div className="flex gap-2">
                  <Input
                    placeholder="Coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    data-testid="coupon-input"
                  />
                  <Button variant="outline" size="sm" onClick={applyCoupon} data-testid="apply-coupon-btn">
                    Apply
                  </Button>
                </div>
                {couponApplied && (
                  <p className="text-green-600 text-sm">
                    Coupon applied: {couponApplied.discount_type === 'percentage' ? `${couponApplied.discount_value}% off` : `₹${couponApplied.discount_value} off`}
                  </p>
                )}

                <Separator />

                <div className="flex justify-between">
                  <span className="text-stone-600">Subtotal</span>
                  <span>{formatPrice(cart.total_cents)}</span>
                </div>

                {calculateDiscount() > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>-{formatPrice(calculateDiscount())}</span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span className="text-stone-600">Shipping</span>
                  <span className="text-curry-700">Free</span>
                </div>

                <Separator />

                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-saffron-600">{formatPrice(finalTotal)}</span>
                </div>

                <Button
                  className="w-full bg-saffron-600 hover:bg-saffron-700 rounded-full"
                  onClick={handlePayment}
                  disabled={loading}
                  data-testid="pay-now-btn"
                >
                  {loading ? 'Processing...' : `Pay ${formatPrice(finalTotal)}`}
                </Button>

                <p className="text-xs text-stone-500 text-center">
                  Secured by Razorpay
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// Orders Page
const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API}/orders`);
        setOrders(response.data);
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [user, navigate]);

  const formatPrice = (cents) => `₹${(cents / 100).toFixed(2)}`;
  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric', month: 'short', day: 'numeric'
  });

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-stone-100 text-stone-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-basmati flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-saffron-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-basmati py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-heading text-3xl font-bold text-stone-900 mb-8" data-testid="orders-title">My Orders</h1>

        {orders.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Package className="h-16 w-16 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500 mb-4">You haven't placed any orders yet</p>
              <Button onClick={() => navigate('/shop')} data-testid="start-shopping-btn">
                Start Shopping
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <Card key={order.id} className="border-0 shadow-sm" data-testid={`order-${order.id}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                    <div>
                      <p className="text-sm text-stone-500">Order ID: {order.id.slice(0, 8)}...</p>
                      <p className="text-sm text-stone-500">{formatDate(order.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge className={getStatusColor(order.status)}>
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      </Badge>
                      <span className="font-bold text-saffron-600">{formatPrice(order.total_cents)}</span>
                    </div>
                  </div>

                  <Separator className="my-4" />

                  <div className="space-y-2">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-stone-600">{item.product_name} x {item.quantity}</span>
                        <span>{formatPrice(item.price_cents * item.quantity)}</span>
                      </div>
                    ))}
                  </div>

                  <Separator className="my-4" />

                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="text-sm text-stone-600">
                      <p className="font-medium">Shipping to:</p>
                      <p>{order.shipping_address.line1}, {order.shipping_address.city}</p>
                      <p>{order.shipping_address.state} - {order.shipping_address.pincode}</p>
                    </div>

                    {order.tracking_number && (
                      <Button variant="outline" size="sm">
                        Track Order
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== MAIN APP ====================

// Payment Callback Page
const PaymentCallbackPage = () => {
  const [status, setStatus] = useState('checking');
  const [message, setMessage] = useState('Verifying payment...');
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchCart } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    const checkPaymentStatus = async () => {
      const searchParams = new URLSearchParams(location.search);
      const orderId = searchParams.get('order_id') || sessionStorage.getItem('pending_order_id');

      if (!orderId) {
        setStatus('error');
        setMessage('Order not found');
        return;
      }

      try {
        // Check payment status
        const response = await axios.get(`${API}/payments/status/${orderId}`);

        if (response.data.status === 'paid' || response.data.payment_state === 'COMPLETED') {
          setStatus('success');
          setMessage('Payment successful! Your order has been placed.');
          await fetchCart();
          sessionStorage.removeItem('pending_order_id');

          // Redirect to orders after 3 seconds
          setTimeout(() => navigate('/orders'), 3000);
        } else if (response.data.payment_state === 'FAILED') {
          setStatus('error');
          setMessage('Payment failed. Please try again.');
        } else {
          setStatus('pending');
          setMessage('Payment is being processed. Please wait...');

          // Poll again after 3 seconds
          setTimeout(checkPaymentStatus, 3000);
        }
      } catch (error) {
        setStatus('error');
        setMessage('Failed to verify payment status');
      }
    };

    if (user) {
      checkPaymentStatus();
    }
  }, [location, navigate, fetchCart, user]);

  return (
    <div className="min-h-screen bg-basmati flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-0 shadow-xl text-center">
        <CardContent className="p-8">
          {status === 'checking' || status === 'pending' ? (
            <>
              <div className="animate-spin h-16 w-16 border-4 border-saffron-600 border-t-transparent rounded-full mx-auto mb-6" />
              <h2 className="font-heading text-xl font-semibold mb-2">{message}</h2>
              <p className="text-stone-500">Please do not close this page</p>
            </>
          ) : status === 'success' ? (
            <>
              <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <Package className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-green-600 mb-2">Payment Successful!</h2>
              <p className="text-stone-500 mb-6">{message}</p>
              <Button onClick={() => navigate('/orders')} className="bg-saffron-600 hover:bg-saffron-700 rounded-full">
                View Orders
              </Button>
            </>
          ) : (
            <>
              <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <X className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-red-600 mb-2">Payment Failed</h2>
              <p className="text-stone-500 mb-6">{message}</p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => navigate('/orders')} className="rounded-full">
                  View Orders
                </Button>
                <Button onClick={() => navigate('/checkout')} className="bg-saffron-600 hover:bg-saffron-700 rounded-full">
                  Try Again
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

function App() {
  return (
    <div className="App min-h-screen flex flex-col bg-basmati">
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Navbar />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/shop" element={<ShopPage />} />
                <Route path="/product/:slug" element={<ProductDetailPage />} />
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/orders" element={<OrdersPage />} />
                <Route path="/payment/callback" element={<PaymentCallbackPage />} />
              </Routes>
            </main>
            <Footer />
          </BrowserRouter>
          <Toaster position="bottom-right" richColors />
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
